import { useEffect, useState, type FormEvent } from "react";
import { api } from "../api";

type Endpoint = {
  id: string;
  name: string;
  url: string;
  events: string[];
  enabled: boolean;
  secretHint: string;
  lastDeliveryAt: string | null;
  lastStatus: number | null;
};

type Delivery = {
  id: string;
  endpointId: string;
  event: string;
  statusCode: number | null;
  success: boolean;
  error: string | null;
  createdAt: string;
};

export function WebhooksPage() {
  const [items, setItems] = useState<Endpoint[]>([]);
  const [deliveries, setDeliveries] = useState<Delivery[]>([]);
  const [events, setEvents] = useState<string[]>([]);
  const [name, setName] = useState("Billing");
  const [url, setUrl] = useState("https://");
  const [selectedEvents, setSelectedEvents] = useState<string[]>([]);
  const [secretOnce, setSecretOnce] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [e, w, d] = await Promise.all([
        api<{ ok: boolean; items: string[] }>("/api/v1/webhooks/events"),
        api<{ ok: boolean; items: Endpoint[] }>("/api/v1/webhooks"),
        api<{ ok: boolean; items: Delivery[] }>("/api/v1/webhooks/deliveries")
      ]);
      setEvents(e.items || []);
      setItems(w.items || []);
      setDeliveries(d.items || []);
      if (!selectedEvents.length && e.items?.length) {
        setSelectedEvents(
          e.items.filter((x) => x.startsWith("account."))
        );
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const toggleEvent = (ev: string) => {
    setSelectedEvents((prev) =>
      prev.includes(ev) ? prev.filter((x) => x !== ev) : [...prev, ev]
    );
  };

  const onCreate = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    setSecretOnce("");
    try {
      const res = await api<{
        ok: boolean;
        endpoint: Endpoint;
        secret: string;
      }>("/api/v1/webhooks", {
        method: "POST",
        body: JSON.stringify({
          name,
          url,
          events: selectedEvents,
          enabled: true
        })
      });
      setSecretOnce(res.secret);
      setUrl("https://");
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="topbar">
        <div>
          <h1>Integrations</h1>
          <p>
            Advanced · webhooks, WHMCS/Blesta billing hooks, and automation.
            Provisioning API:{" "}
            <code className="mono">/api/v1/billing/*</code> — see docs/billing.md.
          </p>
        </div>
        <div className="topbar-actions">
          <button type="button" className="btn btn-ghost" onClick={() => void load()}>
            Refresh
          </button>
        </div>
      </div>

      {error ? <div className="error-banner">{error}</div> : null}
      {secretOnce ? (
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-label">Webhook secret (copy now — shown once)</div>
          <code className="mono">{secretOnce}</code>
        </div>
      ) : null}

      <section className="card" style={{ marginBottom: 16 }}>
        <div className="card-label">Provisioning API (WHMCS / Blesta)</div>
        <p className="card-meta">
          Use a staff API token. Create is idempotent on{" "}
          <code className="mono">externalId</code>.
        </p>
        <pre className="mono" style={{ fontSize: 12, whiteSpace: "pre-wrap" }}>
{`POST /api/v1/billing/accounts
Authorization: Bearer rp_...
{
  "username": "client1",
  "password": "...",
  "packageName": "Starter",
  "externalId": "whmcs:12345",
  "domain": "example.com"
}

POST /api/v1/billing/accounts/suspend  { "externalId": "whmcs:12345" }
POST /api/v1/billing/accounts/terminate { "externalId": "whmcs:12345" }`}
        </pre>
      </section>

      <form className="panel-form" onSubmit={onCreate}>
        <div className="panel-form-title">Add webhook endpoint</div>
        <div className="form-grid">
          <div className="field">
            <label>Name</label>
            <input value={name} onChange={(e) => setName(e.target.value)} required />
          </div>
          <div className="field">
            <label>URL</label>
            <input value={url} onChange={(e) => setUrl(e.target.value)} required />
          </div>
        </div>
        <div style={{ marginBottom: 12 }}>
          <div className="card-label">Events</div>
          <div className="chip-row">
            {events.map((ev) => (
              <label key={ev} className="chip" style={{ cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={selectedEvents.includes(ev)}
                  onChange={() => toggleEvent(ev)}
                />{" "}
                {ev}
              </label>
            ))}
          </div>
        </div>
        <button className="btn btn-primary btn-inline" disabled={busy} type="submit">
          {busy ? "Saving…" : "Create webhook"}
        </button>
      </form>

      <div className="pkg-grid">
        {items.map((ep) => (
          <section key={ep.id} className="card pkg-card">
            <div className="pkg-head">
              <div>
                <div className="card-value" style={{ fontSize: 18 }}>
                  {ep.name}
                </div>
                <p className="card-meta mono">{ep.url}</p>
              </div>
              <span className={`badge ${ep.enabled ? "primary" : ""}`}>
                {ep.enabled ? "enabled" : "disabled"}
              </span>
            </div>
            <p className="card-meta" style={{ fontSize: 12 }}>
              {ep.events.join(", ")}
            </p>
            <p className="mono faint" style={{ fontSize: 11 }}>
              last: {ep.lastStatus ?? "—"} · {ep.lastDeliveryAt
                ? new Date(ep.lastDeliveryAt).toLocaleString()
                : "never"}
            </p>
            <div className="row-actions" style={{ marginTop: 10 }}>
              <button
                type="button"
                className="link-btn"
                onClick={async () => {
                  try {
                    await api(`/api/v1/webhooks/${ep.id}/test`, {
                      method: "POST",
                      body: "{}"
                    });
                    await load();
                  } catch (err) {
                    setError(err instanceof Error ? err.message : String(err));
                  }
                }}
              >
                Test ping
              </button>
              <button
                type="button"
                className="link-btn"
                onClick={async () => {
                  await api(`/api/v1/webhooks/${ep.id}`, {
                    method: "PATCH",
                    body: JSON.stringify({ enabled: !ep.enabled })
                  });
                  await load();
                }}
              >
                {ep.enabled ? "Disable" : "Enable"}
              </button>
              <button
                type="button"
                className="link-btn danger"
                onClick={async () => {
                  if (!confirm("Delete webhook?")) return;
                  await api(`/api/v1/webhooks/${ep.id}`, { method: "DELETE" });
                  await load();
                }}
              >
                Delete
              </button>
            </div>
          </section>
        ))}
      </div>

      <div className="table-wrap" style={{ marginTop: 20 }}>
        <table className="data-table">
          <thead>
            <tr>
              <th>When</th>
              <th>Event</th>
              <th>HTTP</th>
              <th>OK</th>
              <th>Error</th>
            </tr>
          </thead>
          <tbody>
            {deliveries.length === 0 ? (
              <tr>
                <td colSpan={5} className="muted">
                  No deliveries yet.
                </td>
              </tr>
            ) : (
              deliveries.map((d) => (
                <tr key={d.id}>
                  <td className="mono" style={{ fontSize: 11 }}>
                    {new Date(d.createdAt).toLocaleString()}
                  </td>
                  <td className="mono">{d.event}</td>
                  <td className="mono">{d.statusCode ?? "—"}</td>
                  <td>{d.success ? "yes" : "no"}</td>
                  <td className="muted" style={{ fontSize: 12 }}>
                    {d.error || "—"}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}
